export function initTagFilterCounts({ filterContainerSelector, countEndpointView = 'count' } = {}) {
  const cacheLengthHours = 6;
  const maxCacheAge = cacheLengthHours * 60 * 60 * 1000; // 6 hours

  const container = document.querySelector(filterContainerSelector);
  if (!container) return;

  const items = Array.from(container.querySelectorAll('.tag-filters__item'));
  if (!items.length) return;

  function getCachedCount(url) {
    const cached = localStorage.getItem(`tagFilterCountCache::${url}`);
    if (!cached) return null;

    try {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < maxCacheAge) {
        return parsed.count;
      }
    } catch (e) {
      console.warn(`[tag-filter-counts] Failed to parse cache for ${url}`, e);
    }

    return null;
  }

  function saveCachedCount(url, count) {
    try {
      localStorage.setItem(
        `tagFilterCountCache::${url}`,
        JSON.stringify({
          timestamp: Date.now(),
          count,
        })
      );
    } catch (e) {
      console.warn(`[tag-filter-counts] Failed to save cache for ${url}`, e);
    }
  }

  function fetchCount(url) {
    const cached = getCachedCount(url);
    if (cached !== null) {
      return Promise.resolve(cached);
    }

    const fullUrl = url + (url.includes('?') ? '&' : '?') + 'view=' + encodeURIComponent(countEndpointView);

    return fetch(fullUrl, { credentials: 'same-origin' })
      .then((response) => response.text())
      .then((html) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const countEl = tmp.querySelector('#resultcount');
        const count = countEl ? parseInt(countEl.textContent.trim(), 10) : 0;
        const safeCount = isNaN(count) ? 0 : count;

        saveCachedCount(url, safeCount);

        return safeCount;
      })
      .catch((err) => {
        console.error(`[tag-filter-counts] Failed to fetch count for ${url}`, err);
        return 0;
      });
  }

  function applyCount(item, count) {
    const link = item.querySelector('a');
    if (!link) return;

    if (count === 0) {
      item.classList.add('is-disabled');
      link.setAttribute('aria-disabled', 'true');
      link.removeAttribute('href');
    } else {
      item.classList.remove('is-disabled');
      link.removeAttribute('aria-disabled');
      link.setAttribute('href', link.dataset.originalHref);
    }
  }

  function updateCounts() {
    const hrefs = items
      .map((item) => {
        const link = item.querySelector('a');
        if (link) {
          if (!link.dataset.originalHref) {
            link.dataset.originalHref = link.getAttribute('href');
          }
          return link.dataset.originalHref;
        }
        return null;
      })
      .filter(Boolean);

    hrefs.forEach((href, index) => {
      fetchCount(href).then((count) => {
        const item = items[index];
        applyCount(item, count);
      });
    });
  }

  function pruneExpiredCache() {
    const now = Date.now();

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key && key.startsWith('tagFilterCountCache::')) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (!cached.timestamp || now - cached.timestamp >= maxCacheAge) {
            // console.log(`[tag-filter-counts] Removing expired cache for ${key}`);
            localStorage.removeItem(key);
          }
        } catch (e) {
          console.warn(`[tag-filter-counts] Failed to parse cache during prune for ${key}`, e);
          localStorage.removeItem(key); // Remove corrupt entries too
        }
      }
    }
  }

  pruneExpiredCache();
  updateCounts();

  return {
    refresh: updateCounts,
  };
}
